import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Button, Dialog, Input } from "zhihao-ui-react";
import noneOfficerImg from "@/views/assets/images/none-officer.png";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";

export interface assignSubmitData {
    info: any;
    officerId?: number;
    officerIds?: number[];
}

export type AssignSubmitData = assignSubmitData;

export interface Officer {
    user: {
        personId?: number;
        name?: string;
        employeeId?: string;
    };
}

export interface dialogProps {
    info: any;
    title: string;
    officers?: Officer[];
    searchSpecial?: (params: any) => Promise<any>;
    tips: string;
    countFiled?: string;
    curOfficerId?: number;
    curOfficerIds?: number[];
    multiple?: boolean;
    showSelectedCount?: boolean;
    selectedCount?: number;
    countSuffix?: string;
    confirmText?: string;
    searchButtonLabel?: string;
}

export interface AssignDialogRef {
    openDialog: (params: dialogProps) => Promise<void>;
    closeDialog: () => void;
}

export interface AssignDialogProps {
    open?: boolean;
    title?: string;
    info?: any;
    officers?: Officer[];
    searchSpecial?: (params: any) => Promise<any>;
    tips?: string;
    countFiled?: string;
    curOfficerId?: number;
    curOfficerIds?: number[];
    multiple?: boolean;
    showSelectedCount?: boolean;
    selectedCount?: number;
    countSuffix?: string;
    confirmText?: string;
    searchButtonLabel?: string;
    loading?: boolean;
    error?: string | null;
    onClose?: () => void;
    onSubmit?: (data: assignSubmitData) => void | Promise<void>;
    onCurOfficerIdChange?: (value: number | undefined) => void;
    onCurOfficerIdsChange?: (value: number[] | undefined) => void;
}

const DEFAULT_TITLE = "指派项目专员";
const DEFAULT_TIPS = "跟进中的项目数量";

const createDefaultDialogProps = (): dialogProps => ({
    info: {},
    title: DEFAULT_TITLE,
    tips: DEFAULT_TIPS,
    countFiled: "count",
    multiple: false,
    showSelectedCount: false,
    countSuffix: "条",
    confirmText: "确定"
});

/** 指派专员弹窗，保留旧版 openDialog、v-model 选中值和 submit 数据契约。 */
const AssignDialog = forwardRef<AssignDialogRef, AssignDialogProps>(function AssignDialog(
    {
        open,
        title,
        info,
        officers: controlledOfficers,
        searchSpecial: controlledSearchSpecial,
        tips,
        countFiled,
        curOfficerId: controlledOfficerId,
        curOfficerIds: controlledOfficerIds,
        multiple,
        showSelectedCount,
        selectedCount,
        countSuffix,
        confirmText,
        searchButtonLabel = "搜索专员",
        loading = false,
        error,
        onClose,
        onSubmit,
        onCurOfficerIdChange,
        onCurOfficerIdsChange
    },
    ref
) {
    const [visible, setVisible] = useState(Boolean(open));
    const [config, setConfig] = useState<dialogProps>(() => createDefaultDialogProps());
    const [keyword, setKeyword] = useState("");
    const [loadedOfficers, setLoadedOfficers] = useState<Officer[]>(controlledOfficers ?? []);
    const [searchedOfficers, setSearchedOfficers] = useState<Officer[] | undefined>();
    const [currentOfficerId, setCurrentOfficerId] = useState<number | undefined>(controlledOfficerId);
    const [currentOfficerIds, setCurrentOfficerIds] = useState<number[]>(controlledOfficerIds ?? []);
    const [submitLoading, setSubmitLoading] = useState(false);

    useEffect(() => {
        if (open !== undefined) setVisible(open);
    }, [open]);

    useEffect(() => {
        if (controlledOfficers) {
            setLoadedOfficers(controlledOfficers);
            // 受控候选人更新后，显示父组件返回的最新结果，清除旧搜索结果。
            setSearchedOfficers(undefined);
        }
    }, [controlledOfficers]);

    useEffect(() => {
        setCurrentOfficerId(controlledOfficerId);
    }, [controlledOfficerId]);

    useEffect(() => {
        if (controlledOfficerIds !== undefined) setCurrentOfficerIds(controlledOfficerIds);
    }, [controlledOfficerIds]);

    const effectiveConfig = useMemo<dialogProps>(
        () => ({
            ...config,
            title: title ?? config.title,
            info: info ?? config.info,
            searchSpecial: controlledSearchSpecial ?? config.searchSpecial,
            tips: tips ?? config.tips,
            countFiled: countFiled ?? config.countFiled,
            curOfficerId: controlledOfficerId ?? config.curOfficerId,
            curOfficerIds: controlledOfficerIds ?? config.curOfficerIds,
            multiple: multiple ?? config.multiple,
            showSelectedCount: showSelectedCount ?? config.showSelectedCount,
            selectedCount: selectedCount ?? config.selectedCount,
            countSuffix: countSuffix ?? config.countSuffix,
            confirmText: confirmText ?? config.confirmText,
            searchButtonLabel: searchButtonLabel ?? config.searchButtonLabel
        }),
        [
            config,
            controlledOfficerId,
            controlledOfficerIds,
            controlledSearchSpecial,
            countFiled,
            info,
            multiple,
            selectedCount,
            showSelectedCount,
            countSuffix,
            confirmText,
            searchButtonLabel,
            title,
            tips
        ]
    );

    const getSpecialList = useCallback(
        async (searchSpecial: dialogProps["searchSpecial"], searchKeyword: string) => {
            if (!searchSpecial) {
                // 没有远程搜索接口的场景（如专区合同）沿用 Vue 的本地姓名过滤。
                const source = controlledOfficers ?? loadedOfficers;
                setSearchedOfficers(
                    source.filter(officer =>
                        (officer.user.name || "").toLowerCase().includes(searchKeyword.toLowerCase())
                    )
                );
                return;
            }
            try {
                const result = await searchSpecial(searchKeyword);
                setSearchedOfficers(Array.isArray(result) ? (result as Officer[]) : []);
            } catch {
                setSearchedOfficers([]);
            }
        },
        [controlledOfficers, loadedOfficers]
    );

    const closeDialog = useCallback(() => {
        setVisible(false);
        setKeyword("");
        setLoadedOfficers([]);
        setSearchedOfficers(undefined);
        setConfig(createDefaultDialogProps());
        setCurrentOfficerId(undefined);
        setCurrentOfficerIds([]);
        setSubmitLoading(false);
        onCurOfficerIdChange?.(undefined);
        onCurOfficerIdsChange?.(undefined);
        onClose?.();
    }, [onClose, onCurOfficerIdChange, onCurOfficerIdsChange]);

    const openDialog = useCallback(
        async (params: dialogProps) => {
            // 命令式调用与受控调用都补齐默认按钮文案，避免未显式传 confirmText 时渲染无名称的提交按钮。
            setConfig({ ...createDefaultDialogProps(), ...params });
            setKeyword("");
            setLoadedOfficers(params.officers ?? []);
            setSearchedOfficers(undefined);
            setCurrentOfficerId(params.curOfficerId);
            setCurrentOfficerIds(params.curOfficerIds ?? []);
            onCurOfficerIdChange?.(params.curOfficerId);
            onCurOfficerIdsChange?.(params.curOfficerIds);
            setVisible(true);
            // 命令式打开且未配置远程搜索时，直接展示传入的候选人列表。
            if (params.searchSpecial) await getSpecialList(params.searchSpecial, "");
        },
        [getSpecialList, onCurOfficerIdChange, onCurOfficerIdsChange]
    );

    useImperativeHandle(ref, () => ({ openDialog, closeDialog }), [closeDialog, openDialog]);

    const selectOfficer = (officerId: number) => {
        if (effectiveConfig.multiple) {
            const nextIds = currentOfficerIds.includes(officerId)
                ? currentOfficerIds.filter(id => id !== officerId)
                : [...currentOfficerIds, officerId];
            setCurrentOfficerIds(nextIds);
            onCurOfficerIdsChange?.(nextIds);
            return;
        }
        setCurrentOfficerId(officerId);
        onCurOfficerIdChange?.(officerId);
    };

    const submit = async () => {
        if (!hasSelection) return;
        const data: assignSubmitData = { info: effectiveConfig.info };
        if (effectiveConfig.multiple) {
            data.officerIds = currentOfficerIds;
        } else {
            data.officerId = Number(currentOfficerId ?? effectiveConfig.curOfficerId);
        }
        setSubmitLoading(true);
        try {
            await onSubmit?.(data);
            closeDialog();
        } catch {
            // 提交失败时保留弹框，调用方可以在页面中展示具体失败原因。
        } finally {
            setSubmitLoading(false);
        }
    };

    const searchSpecial = effectiveConfig.searchSpecial;
    // 远程搜索由父组件回填候选；无远程接口时则使用弹窗内的本地姓名过滤结果。
    const displayedOfficers = searchSpecial
        ? (controlledOfficers ?? searchedOfficers ?? loadedOfficers)
        : (searchedOfficers ?? controlledOfficers ?? loadedOfficers);
    const selectedIds = effectiveConfig.multiple
        ? currentOfficerIds
        : [currentOfficerId ?? effectiveConfig.curOfficerId];
    const hasSelection = effectiveConfig.multiple ? currentOfficerIds.length > 0 : Boolean(selectedIds[0]);
    const countField = effectiveConfig.countFiled ?? "count";
    return (
        <Dialog
            open={visible}
            modalClass="assign-dialog-modal"
            title={effectiveConfig.title}
            width={842}
            onClose={closeDialog}
            footer={
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <Button onClick={closeDialog}>取消</Button>
                    <Button disabled={!hasSelection || submitLoading} type="primary" onClick={() => void submit()}>
                        {effectiveConfig.confirmText}
                    </Button>
                </div>
            }
        >
            <div className="assign-dialog-search-row">
                <Input
                    aria-label="专员姓名"
                    className="assign-dialog-search-input"
                    value={keyword}
                    placeholder="请输入姓名"
                    onChange={event => setKeyword(event.target.value)}
                    onKeyDown={event => {
                        if (event.key === "Enter") void getSpecialList(searchSpecial, keyword);
                    }}
                />
                <Button
                    aria-label={effectiveConfig.searchButtonLabel || "搜索专员"}
                    className="assign-dialog-search-button"
                    title={effectiveConfig.searchButtonLabel || "搜索专员"}
                    onClick={() => void getSpecialList(searchSpecial, keyword)}
                >
                    <TableSearchIcon name="search" />
                </Button>
            </div>
            {error ? <Alert message={error} role="alert" type="error" /> : null}
            {effectiveConfig.showSelectedCount && effectiveConfig.selectedCount !== undefined ? (
                <div style={{ marginTop: 12 }}>已选择记录条数: {effectiveConfig.selectedCount} 条</div>
            ) : null}
            {effectiveConfig.multiple ? (
                <div style={{ marginTop: 12, color: "rgba(0, 0, 0, 0.65)" }}>
                    请选择需要被分配的人员，确认后，系统将按选择的人员顺序依次将记录指派给每个人。
                </div>
            ) : null}
            {displayedOfficers.length > 0 ? (
                <div className="assign-dialog-list" aria-label="专员列表" aria-busy={loading} role="radiogroup">
                    {displayedOfficers.map(officer => {
                        const officerId = officer.user.personId;
                        if (officerId == null) return null;
                        const selected = selectedIds.includes(officerId);
                        const order = effectiveConfig.multiple ? currentOfficerIds.indexOf(officerId) + 1 : 0;
                        const officerRecord = officer as Officer & Record<string, unknown>;
                        const officerCount = officerRecord[countField] ?? officerRecord.count ?? "";
                        return (
                            <button
                                key={officerId}
                                aria-checked={selected}
                                aria-label={officer.user.name || String(officerId)}
                                className={`assign-dialog-card${selected ? " is-selected" : ""}`}
                                role="radio"
                                type="button"
                                onClick={() => selectOfficer(officerId)}
                            >
                                <span className="assign-dialog-card-info">
                                    <span className="assign-dialog-card-name-row">
                                        <span className="assign-dialog-card-name">
                                            {officer.user.name || "未命名专员"}
                                            {effectiveConfig.multiple && order > 0 ? (
                                                <strong style={{ marginLeft: 8, color: "#3370ff" }}>{order}</strong>
                                            ) : null}
                                        </span>
                                        <span className="assign-dialog-employee-id">
                                            {officer.user.employeeId || ""}
                                        </span>
                                    </span>
                                    <span className="assign-dialog-follow-up">
                                        {effectiveConfig.tips} <b>{String(officerCount)}</b>{" "}
                                        {effectiveConfig.countSuffix}
                                    </span>
                                </span>
                                {effectiveConfig.multiple ? (
                                    <input
                                        type="checkbox"
                                        checked={selected}
                                        onChange={() => selectOfficer(officerId)}
                                        onClick={event => event.stopPropagation()}
                                        aria-label={`选择${officer.user.name}`}
                                    />
                                ) : (
                                    <span aria-hidden="true" className="assign-dialog-radio">
                                        {selected ? <span /> : null}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 20 }}>
                    <img
                        src={noneOfficerImg}
                        alt="暂无可指派服务人员"
                        style={{ width: 312, height: 233, objectFit: "contain" }}
                    />
                    <span
                        style={{
                            marginTop: 30,
                            color: "#a9b5c2",
                            fontSize: 18,
                            fontWeight: 500,
                            lineHeight: "20px"
                        }}
                    >
                        暂未设定可指派的服务人员
                    </span>
                </div>
            )}
        </Dialog>
    );
});

export { AssignDialog };
export default AssignDialog;
